import { toast } from "@ui/molecules/Toasts/toast";
import { getErrorMessage } from "@utils/getErrorMessage";
import { createSignal } from "solid-js";
import { subscribeToFreeAskPage } from "@/client";
import { signInUrl } from "~/modules/shared/utils/auth";
import { viewerIsSubscribed } from "./askPageAccess";
import { askPageVars } from "./pageVars";

type Options = {
	onPaidJoin: () => void;
	/** Given, a free subscription updates the page in place rather than reloading it. */
	onFreeJoined?: () => void;
	displayName: string;
};

/**
 * Join / subscribe from the AskPage chrome. Paid pages open the tier/paywall
 * flow; unpaid pages POST a free entitlement via the generated API client.
 */
export const useAskPageJoin = (options: Options) => {
	const [busy, setBusy] = createSignal(false);
	const [error, setError] = createSignal<string | null>(null);

	const join = async () => {
		const vars = askPageVars();
		setError(null);

		if (!vars.isSignedIn) {
			window.location.href = signInUrl();
			return;
		}

		if (vars.is_paid_ask_page) {
			options.onPaidJoin();
			return;
		}

		if (viewerIsSubscribed(vars.status)) {
			return;
		}

		setBusy(true);
		try {
			const res = await subscribeToFreeAskPage({
				body: { handle: vars.influencer_handle },
			});
			if (res.error) {
				setError(
					getErrorMessage(res.error, "Could not subscribe. Try again."),
				);
				return;
			}

			toast.success({
				title: "Subscribed",
				description: `You're now subscribed to ${options.displayName}.`,
			});
			if (options.onFreeJoined) {
				options.onFreeJoined();
			} else {
				window.location.reload();
			}
		} catch (thrown) {
			setError(getErrorMessage(thrown, "Could not subscribe. Try again."));
		} finally {
			setBusy(false);
		}
	};

	return { join, busy, error };
};
